import { inject } from '@angular/core';
import { Auth } from '@fido/firebase/auth';
import { ref, Storage, uploadBytesResumable } from '@fido/firebase/storage';
import { createInjectionToken } from 'ngxtension/create-injection-token';
import { injectCurrentOrganization } from '../../organizations/data/current-organization';

export interface UploadProgress {
  readonly bytesTransferred: number;
  readonly totalBytes: number;
  readonly percent: number;
  readonly storagePath: string;
}

export const [injectUploadContactsFile, provideUploadContactsFile] = createInjectionToken(() => {
  const storage = inject(Storage);
  const auth = inject(Auth);
  const organization = injectCurrentOrganization();

  return (
    file: File,
    onProgress: (progress: UploadProgress) => void,
  ): Promise<{ storagePath: string }> => {
    const org = organization.value();
    const uid = auth.currentUser?.uid;
    if (!org) return Promise.reject(new Error('No organization'));
    if (!uid) return Promise.reject(new Error('Not authenticated'));

    // Unique filename: timestamp + original name to avoid collisions
    const storagePath = `organizations/${org.id}/contacts-imports/${uid}/${Date.now()}_${file.name.replaceAll(/[^a-zA-Z0-9._-]/g, '_')}`;

    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(ref(storage, storagePath), file, {
        contentType: file.type,
      });

      task.on(
        'state_changed',
        (snapshot) => {
          const percent =
            snapshot.totalBytes > 0
              ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
              : 0;
          onProgress({
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            percent,
            storagePath,
          });
        },
        (err) => reject(err),
        () => resolve({ storagePath }),
      );
    });
  };
});
